import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@ordo/ui/accordion'
import { Alert, AlertDescription, AlertTitle } from '@ordo/ui/alert'
import { Card } from '@ordo/ui/card'
import { cn } from '@ordo/ui/cn'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@ordo/ui/select'
import { Separator } from '@ordo/ui/separator'
import { Flame, Maximize2, ShoppingBag, Snowflake, Thermometer, X } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'

import { AssetImage } from '@/components/asset-image'
import { Badge } from '@/components/chefs/badge'
import { Button } from '@/components/chefs/button'
import { CHIP_CLASS_SM, ChipButton } from '@/components/chefs/chip-button'
import { ComponentRecipeBlock, type RecipeScale } from '@/components/chefs/recipe-detail-sections'
import { useMeasurementPreference } from '@/hooks/chefs/useMeasurementPreference'
import { useSubstitutionModal } from '@/hooks/chefs/useSubstitutionModal'
import { isSingleElementDeliverable, visibleComponentCount } from '@/lib/chefs/deliverable-detail'
import { temperatureLabel } from '@/lib/chefs/format'
import { packingPanelPlans } from '@/lib/chefs/packing'
import { multiplierOptions, type UnitSystem } from '@/lib/chefs/recipe-quantities'
import type { Component, Deliverable } from '@/lib/chefs/types'
import { DIET_LABEL, MEALTIME_LABEL, PRODUCT_TYPE_LABEL } from '@/lib/chefs/types'

import { CHEF_SERVICE_MATERIALS_VISIBLE } from '../../../../shared/chef-packaging-visibility'

interface DeliverableDetailPanelProps {
  deliverable: Deliverable
  onClose: () => void
  onFullscreen?: (deliverable: Deliverable) => void
  variant?: 'panel' | 'fullscreen'
}

function AttrRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex items-baseline justify-between py-1 text-sm">
      <span className="font-medium text-foreground">{label}</span>
      <span className="text-right text-foreground">{value}</span>
    </div>
  )
}

function StackedAttr({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1.5 py-1.5 text-sm">
      <span className="font-medium text-foreground">{label}</span>
      <div className="flex flex-wrap items-center gap-1.5">{children}</div>
    </div>
  )
}

function ComponentBlock({ component, scale }: { component: Component; scale: RecipeScale }) {
  const openSubstitutionModal = useSubstitutionModal((s) => s.openModal)
  // Only a recipe-less condiment collapses to a flat ×N row; a house-made
  // condiment gets the same expandable recipe section as any other component
  // (its recipe was previously unreachable from this panel).
  if (component.isCondiment && !component.recipe) {
    return (
      <div className="flex items-center justify-between border-b border-border px-5 py-4 text-base last:border-b-0">
        <span className="font-medium">{component.name}</span>
        <span className="text-sm text-muted-foreground">×{component.condimentQty ?? 1}</span>
      </div>
    )
  }

  return (
    <AccordionItem value={component.id} className="border-b border-border last:border-b-0">
      <AccordionTrigger className="px-5 py-4 text-base font-medium hover:no-underline">
        {component.name}
      </AccordionTrigger>
      <AccordionContent className="space-y-3 px-5 pb-5">
        <ComponentRecipeBlock
          component={component}
          scale={scale}
          onSubstituteIngredient={() => openSubstitutionModal({ view: 'form' })}
        />
      </AccordionContent>
    </AccordionItem>
  )
}

/**
 * The Packing section (PROD-1500, Figma 156:965): how many servings fit in one
 * container ("98 servings per Standard Hotel Pan", worked out from the
 * catalog's package sizes and portion volumes), then one line per container
 * ("Pan 1 · 98 servings"). Nothing without a plan.
 */
export function DeliverablePackingSection({
  packing,
  packedWith,
}: {
  packing?: Deliverable['packing']
  packedWith?: string[]
}) {
  const plans = packingPanelPlans(packing ?? [])
  if (plans.length === 0 && !(packedWith && packedWith.length > 0)) {
    return null
  }
  return (
    <section data-testid="deliverable-packing" className="flex flex-col gap-2 border-t border-border px-6 py-5">
      <h3 className="text-sm font-medium text-foreground">Packing</h3>
      {plans.map((plan) => (
        <div key={plan.key} className="flex flex-col gap-1.5">
          <p className="text-sm text-foreground">{plan.heading}</p>
          {plan.containers.length > 0 ? (
            <ul className="flex flex-col divide-y divide-border rounded-md border border-border text-sm">
              {plan.containers.map((container) => (
                <li key={container.label} className="flex items-baseline gap-3 px-3 py-2">
                  <span className="w-16 shrink-0 font-medium text-foreground">{container.label}</span>
                  <span className="min-w-0 flex-1 text-foreground">{container.content}</span>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
      {packedWith && packedWith.length > 0 ? (
        <p className="text-sm text-muted-foreground">Packed with {packedWith.join(', ')}</p>
      ) : null}
    </section>
  )
}

export function DeliverableDetailPanel({
  deliverable,
  onClose,
  onFullscreen,
  variant = 'panel',
}: DeliverableDetailPanelProps) {
  const diets = deliverable.diets ?? []
  const componentCount = visibleComponentCount(deliverable)
  const isSingleElement = isSingleElementDeliverable(deliverable)
  const onlyComponent = deliverable.components[0] ?? null
  const TempIcon =
    deliverable.temperature === 'hot' ? Flame : deliverable.temperature === 'ambient' ? Thermometer : Snowflake
  const isFullscreen = variant === 'fullscreen'
  const openSubstitutionModal = useSubstitutionModal((s) => s.openModal)

  // Recipe scaling: default to prepping the full order count, imperial units.
  const orderCount = Math.max(1, Math.round(deliverable.qty))
  const [servings, setServings] = useState(orderCount)
  const [unitSystem, setUnitSystem] = useState<UnitSystem>('imperial')
  useEffect(() => {
    setServings(orderCount)
  }, [deliverable.id, orderCount])
  const servingOptions = useMemo(() => multiplierOptions(orderCount), [orderCount])
  // Kitchen-wide Standard | Technical (Settings > My Kitchen). Technical reads
  // the authored unit, so the imperial/metric choice has nothing to convert.
  const { mode: measurementMode } = useMeasurementPreference()
  const isTechnical = measurementMode === 'technical'
  const scale: RecipeScale = { servings, system: unitSystem, mode: measurementMode }

  return (
    <Card
      className={cn(
        'flex h-full w-full flex-col overflow-hidden bg-background p-0',
        isFullscreen ? 'border-0 shadow-none' : 'shrink-0 border-0 border-border lg:rounded-2xl lg:border lg:shadow-xs',
      )}>
      <div className="flex flex-1 flex-col overflow-y-auto">
        <div className="sticky top-0 z-10 flex items-start justify-between gap-3 bg-background px-6 pb-3 pt-5">
          <span className="flex min-w-0 flex-wrap items-center gap-2 pr-2">
            <h2 className="text-xl font-medium leading-tight">{deliverable.name}</h2>
            {deliverable.isFieldTrip ? (
              <Badge variant="info" className="shrink-0 gap-1 rounded-full px-2 py-0.5 text-[11px]">
                <ShoppingBag className="h-3 w-3" strokeWidth={1.75} />
                Field trip
              </Badge>
            ) : null}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon-sm"
            aria-label="Close"
            className="-mr-2 -mt-1 text-muted-foreground"
            onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>

        <div className="flex flex-wrap items-center gap-2 px-6 pb-5">
          <Select value={String(servings)} onValueChange={(v) => setServings(Number(v))}>
            <SelectTrigger aria-label="Scale recipe to servings" className={cn('w-auto', CHIP_CLASS_SM)}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {servingOptions.map((n) => (
                <SelectItem key={n} value={String(n)}>
                  {n}x
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={unitSystem} onValueChange={(v) => setUnitSystem(v as UnitSystem)} disabled={isTechnical}>
            <SelectTrigger
              aria-label="Unit system"
              title={isTechnical ? 'This kitchen reads amounts as entered (Settings > My Kitchen)' : undefined}
              className={cn('w-auto', CHIP_CLASS_SM)}>
              <SelectValue>{isTechnical ? 'As entered' : undefined}</SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="imperial">Imperial (lbs, oz)</SelectItem>
              <SelectItem value="metric">Metric (kg, g)</SelectItem>
            </SelectContent>
          </Select>
          {!isFullscreen ? (
            <ChipButton
              Icon={Maximize2}
              className={cn(CHIP_CLASS_SM, 'hidden lg:inline-flex')}
              onClick={() => onFullscreen?.(deliverable)}>
              Full screen
            </ChipButton>
          ) : null}
        </div>

        {deliverable.isFieldTrip ? (
          <div className="px-6 pb-5">
            <Alert variant="info" className="flex items-start gap-2.5">
              <ShoppingBag className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.75} />
              <div>
                <AlertTitle>Field trip — needs to be bagged</AlertTitle>
                <AlertDescription>
                  This order leaves campus for a field trip. Pack each serving as an individual bagged{' '}
                  {deliverable.mealtime ? MEALTIME_LABEL[deliverable.mealtime].toLowerCase() : 'meal'}, with the drink
                  in the bag too.
                </AlertDescription>
              </div>
            </Alert>
          </div>
        ) : null}

        <div className="flex flex-col gap-5 px-6 pb-6 lg:flex-row">
          {deliverable.imageUrl ? (
            <img
              src={deliverable.imageUrl}
              alt={deliverable.name}
              className="h-[240px] w-full shrink-0 rounded-xl bg-muted object-cover lg:w-[240px]"
            />
          ) : (
            <div className="h-[240px] w-full shrink-0 rounded-xl bg-muted lg:w-[240px]" />
          )}

          <div className="flex flex-1 flex-col">
            <AttrRow label="Type" value={PRODUCT_TYPE_LABEL[deliverable.productType]} />
            {deliverable.servingSize ? <AttrRow label="Serving Size" value={deliverable.servingSize} /> : null}
            {deliverable.packaging ? (
              <AttrRow
                label="Packaging"
                value={
                  <span className="flex items-center justify-end gap-2">
                    <span>{deliverable.packaging}</span>
                    {deliverable.packagingImageUrl ? (
                      // Reference photo so a chef can confirm the container by
                      // sight (PROD-1216). Purely confirmatory — the name stays
                      // the source of truth, so a broken/absent image renders
                      // nothing rather than a placeholder competing with the
                      // deliverable's own hero image above.
                      <AssetImage
                        src={deliverable.packagingImageUrl}
                        alt={`Packaging: ${deliverable.packaging}`}
                        className="h-14 w-14 shrink-0 rounded-md border border-border bg-muted object-cover"
                        fallback={null}
                      />
                    ) : null}
                  </span>
                }
              />
            ) : null}
            {CHEF_SERVICE_MATERIALS_VISIBLE && deliverable.supplies && deliverable.supplies.length > 0 ? (
              <AttrRow
                label="Supplies"
                value={
                  <span className="flex flex-col items-end gap-0.5">
                    {deliverable.supplies.map((row) => (
                      <span key={row.skuId}>
                        {row.qty.toLocaleString()}× {row.name}
                      </span>
                    ))}
                  </span>
                }
              />
            ) : null}
            {componentCount !== null ? <AttrRow label="Components" value={componentCount} /> : null}

            <Separator className="my-2" />

            {diets.length > 0 ? (
              <StackedAttr label="Dietary">
                {diets.map((d) => (
                  <Badge
                    key={d}
                    variant={d === 'dairy-free' ? 'dairy-free' : 'vegetarian'}
                    className="rounded-full px-2 py-0.5 text-[11px]">
                    {DIET_LABEL[d]}
                  </Badge>
                ))}
              </StackedAttr>
            ) : null}

            <StackedAttr label="Temperature">
              <Badge variant={deliverable.temperature} className="gap-1 px-2">
                <TempIcon className="h-3 w-3" strokeWidth={1.75} />
                {temperatureLabel(deliverable.temperature)}
              </Badge>
            </StackedAttr>
          </div>
        </div>

        <DeliverablePackingSection packing={deliverable.packing} packedWith={deliverable.packedWith} />

        {isSingleElement ? (
          <div className="border-t border-border px-5 py-5">
            {onlyComponent ? (
              <ComponentRecipeBlock
                component={onlyComponent}
                scale={scale}
                onSubstituteIngredient={() => openSubstitutionModal({ view: 'form' })}
              />
            ) : (
              <p className="text-sm text-muted-foreground">No recipe on file for this deliverable.</p>
            )}
          </div>
        ) : (
          <Accordion
            type="multiple"
            // Chefs read every recipe while prepping — a multi-item package
            // opens with EVERY component expanded rather than a wall of
            // collapsed headers (Sheila 2026-08-03).
            defaultValue={deliverable.components.map((c) => c.id)}
            className="border-t border-border">
            {deliverable.components.map((c) => (
              <ComponentBlock key={c.id} component={c} scale={scale} />
            ))}
          </Accordion>
        )}
      </div>
    </Card>
  )
}
